import { useState, useEffect } from 'react';

/** Matches Tailwind's `lg` breakpoint. Kept here so the JS and the CSS cannot disagree. */
const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * True at Tailwind's `lg` breakpoint and up.
 *
 * WHY THIS EXISTS RATHER THAN A MEDIA QUERY. The info panel is a modal bottom sheet on phones
 * and an inline expanding panel on desktop. Those differ in behavior, not just appearance: the
 * sheet traps focus, closes on Escape, and locks body scroll, and the panel must do none of
 * those things. CSS can hide a focus trap but it cannot switch one off, so rendering both and
 * toggling visibility would leave a trap active on desktop with nothing visible to escape from.
 *
 * Defaults to `false` when matchMedia is unavailable. That yields the sheet, which is the safe
 * direction: a sheet on a wide screen is merely unusual, whereas an inline panel that some code
 * path believes is modal is a keyboard trap.
 */
export default function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event) => setIsDesktop(event.matches);

    // Re-read on mount as well as on change: between the initial state above and this effect
    // running, the viewport may already have changed (device rotation during hydration).
    setIsDesktop(mql.matches);

    // addEventListener is the modern API; addListener is kept for Safari below 14, which is
    // still a non-trivial share of the phones that reach a checkout page.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }

    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isDesktop;
}
